import { useTranslation } from 'react-i18next';
import { Droplets, Zap } from 'lucide-react';
import { useComando, useDevice, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { Avviso, Badge, Bottone, Card, Pagina, StatoComandoVista, Stepper, Valore, stili } from '@/ui';
import { useValoreDifferito, useValoreFunzione } from '../comune';
import s from '../features.module.css';

export function SanitarioPage() {
  const { t } = useTranslation();
  const f = useFormato();
  const { invia } = useDevice();
  const impianto = useStato((st) => st.impianto);
  const sanitario = useStato((st) => st.stato?.sanitario);
  const { obsoleto } = useObsoleto();
  const acs = useValoreFunzione('ACCUMULO_SANITARIO');
  const comando = useComando('sanitario');
  const giorni = t('giorni', { returnObjects: true }) as unknown as string[];
  const setpoint = useValoreDifferito(sanitario?.setpoint ?? impianto?.sanitario.setpoint ?? 48, (v) => invia({ azione: 'set_sanitario', setpoint: v }));

  if (!impianto) return null;
  if (!impianto.sanitario.presente) {
    return (
      <Pagina titolo={t('sanitario.titolo')}>
        <Avviso tono="info">{t('sanitario.nonPresente')}</Avviso>
      </Pagina>
    );
  }

  // Read-only source (branded thermostats via the vendor cloud): the hot water
  // is on/off with no temperature (ADR-060). Show its state, not a control —
  // there is nothing to command here, and a temperature slider would be a lie.
  if (sanitario?.soloOnOff) {
    const acceso = sanitario.inCarica;
    const boostFinoA = sanitario.boostFinoA ?? null;
    return (
      <Pagina titolo={t('sanitario.titolo')}>
        <Card titolo={t('sanitario.titolo')} icona={<Droplets size={20} aria-hidden />}>
          <div className={s.letturaRiga}>
            <Badge tono={acceso ? 'info' : 'ok'}>{acceso ? t('sanitario.accesa') : t('sanitario.spenta')}</Badge>
            {boostFinoA && <Badge tono="info">{t('sanitario.boostAttivo', { ora: f.ora(boostFinoA) })}</Badge>}
          </div>
          <p className={s.letturaNota}>{t('sanitario.solaLetturaNota')}</p>
        </Card>
      </Pagina>
    );
  }

  const boost = sanitario?.boostFinoA ?? null;
  const p = impianto.parametri.sanitario.antilegionella;

  return (
    <Pagina titolo={t('sanitario.titolo')}>
      <div className={s.grigliaDue}>
        <Card titolo={t('sanitario.accumulo')} icona={<Droplets size={20} aria-hidden />}>
          <div className={s.letturaRiga}>
            <Valore numero={f.tempNumero(acs.valore)} unita={acs.valore === null ? undefined : f.unitaTemp} dimensione="primario" obsoleto={acs.obsoleto} />
            {!obsoleto && <Badge tono={sanitario?.inCarica ? 'info' : 'ok'}>{sanitario?.inCarica ? t('sanitario.inCarica') : t('sanitario.aRegime')}</Badge>}
          </div>
          {acs.q === 'sonda_scollegata' && <Avviso tono="warn">{t('stato.sondaScollegata')}</Avviso>}
          <p className={s.letturaNota}>{t('sanitario.antilegionella', { giorno: giorni[p.giorno], ora: String(p.ora).padStart(2, '0'), temp: f.temp(p.temperatura, 0) })}</p>
        </Card>
        <Card titolo={t('sanitario.setpoint')}>
          <Stepper
            dimensione="primario"
            valore={f.tempNumero(setpoint.valore, 0)}
            onMeno={() => setpoint.imposta(Math.max(35, setpoint.valore - 1))}
            onPiu={() => setpoint.imposta(Math.min(65, setpoint.valore + 1))}
            etichettaMeno={t('sanitario.meno')}
            etichettaPiu={t('sanitario.piu')}
            disabilitato={obsoleto}
          />
          <div className={stili.riga}>
            <Bottone variante={boost ? 'pericolo' : 'primario'} onClick={() => void invia({ azione: 'set_sanitario', boost: !boost })} disabled={obsoleto}>
              <Zap size={22} aria-hidden />
              {boost ? t('sanitario.boostAnnulla') : t('sanitario.boostAvvia', { minuti: impianto.sanitario.boostMinuti })}
            </Bottone>
            {boost && <Badge tono="info">{t('sanitario.boostAttivo', { ora: f.ora(boost) })}</Badge>}
          </div>
          <StatoComandoVista esito={setpoint.inAttesa && !comando ? 'in_invio' : comando?.esito} motivo={comando?.motivo} />
        </Card>
      </div>
    </Pagina>
  );
}
